import { PROGRAMMING_LANG } from "../../constants/code-block.constant";
import { IconCheck, IconClipboard, IconDownload } from "@tabler/icons-react";
import { FC, memo, useContext, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { v4 as uuidv4 } from "uuid";
import { ThemeContext } from "../../store/theme.store";

/**
 * Props for the CodeBlock component
 */
interface Props {
  /** Programming language for syntax highlighting */
  language: string;
  /** Code content to display */
  value: string;
}

/**
 * CodeBlock component displays code with syntax highlighting using Prism.
 * Includes a header with language label and copy button. Shows "Copied!" feedback
 * after copying. Adapts to dark/light theme.
 * 
 * @component
 * @param {Props} props - Component props
 * @param {string} props.language - Programming language
 * @param {string} props.value - Code content
 * @returns {JSX.Element} Code block with syntax highlighting and copy functionality
 * 
 * @example
 * ```tsx
 * <CodeBlock language="typescript" value="const x = 1;" />
 * ```
 */
export const CodeBlock: FC<Props> = memo(({ language, value }) => {
  const [isCopied, setIsCopied] = useState<Boolean>(false);
  const { state, dispatch } = useContext(ThemeContext);

  const copyToClipboard = () => {
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      return;
    }

    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    });
  };

  return (
    <div className="codeblock relative font-sans text-[16px] mb-3">
      <div className="flex items-center justify-between py-1.5 px-4">
        <span
          className={`text-xs lowercase code-block-header ${
            state.isDarkTheme ? "text-white" : "text-black"
          }`}
        >
          {language}
        </span>

        <div className={`flex items-center`}>
          <button
            className={`flex gap-1.5 items-center bg-none p-1 text-xs   ${
              state.isDarkTheme ? "text-white" : "text-black"
            }`}
            onClick={copyToClipboard}
          >
            {isCopied ? (
              <IconCheck size={18} />
            ) : (
              <IconClipboard
                size={18}
                color={state.isDarkTheme ? "white" : "black"}
              />
            )}
            {isCopied ? "Copied!" : "Copy code"}
          </button>
        </div>
      </div>

      <SyntaxHighlighter
        language={language}
        style={oneDark}
        customStyle={{ margin: 0 }}
      >
        {value}
      </SyntaxHighlighter>
    </div>
  );
});
CodeBlock.displayName = "CodeBlock";
